import React, { FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface ImageOverlayProps {
  alt: string;
  getFileUrl: () => Promise<string>;
}

const ImageOverlay: FC<ImageOverlayProps> = ({ alt, getFileUrl }) => {
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = async () => {
    setOpen(true);
    if (!imageUrl) {
      setLoading(true);
      setError(null);
      try {
        const url = await getFileUrl();
        setImageUrl(url);
      } catch (err) {
        console.error("Failed to get image URL:", err);
        setError("Failed to load image.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <IconButton onClick={handleOpen} color="primary">
        <VisibilityIcon />
      </IconButton>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{alt}</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={alt}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                margin: '0 auto',
              }}
            />
          ) : (
            <Typography>No image to display.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageOverlay;
